import { Injectable } from '@angular/core';
import { FlowTypes } from '@interfaces/models/flow-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { MemoizedSelector, Store } from '@ngrx/store';
import { SaveDraftItem } from '@shared/components/save-draft-modal/save-draft-modal.model';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { minorDraftIdsToDiscardOnExit } from '@shared/helpers/stale-minor-draft.helper';
import {
  CommunicationChannel,
  DraftInitiator,
  DraftType,
  ExperienceType,
} from '@shared/models/draft';
import { LoggerCodes } from '@shared/modules/logger/constants/logger.constants';
import { DraftService, RetrieveDraftItem } from '@shared/services/draft/draft.service';
import { HydrationResult } from '@shared/services/hydration/hydration.models';
import { hydrate } from '@shared/services/hydration/hydration.service';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { StopPendingEffectsService } from '@src/app/core/services/stop-pending-effects.service';
import { LogError, LogInfo } from '@store/actions';
import {
  AddStateToTransactions,
  LoadTransactionStates,
  SetTransactionId,
  StampDraftOnTransaction,
} from '@store/actions/adapted-state/adapted-state.actions';
import {
  ActiveDraftChanged,
  ActiveDraftChangedFailure,
  ActiveDraftChangedSuccess,
  DeleteDrafts,
  DeleteDraftsFailure,
  DeleteDraftsStarted,
  DeleteDraftsSuccess,
  DraftResumeDeclined,
  HydrateDraft,
  HydrateDraftFailure,
  HydrateDraftSuccess,
  MinorDraftLookupTimedOut,
  MinorFlowEntered,
  RetrieveDrafts,
  RetrieveDraftsFailure,
  RetrieveDraftsSuccess,
  SaveDraftOnQuit,
  SaveNewDraft,
  SaveNewDraftFailure,
  SaveNewDraftSuccess,
  StaleDraftDiscarded,
  StaleMinorDraftCleanupRequested,
  UpdateDraft,
  UpdateDraftFailure,
  UpdateDraftSuccess,
} from '@store/actions/draft/draft.actions';
import { Next } from '@store/actions/router/router.actions';
import { RouterStateUrl, State, TransactionState } from '@store/reducers';
import {
  selectBaseProcessTrackingId,
  selectDraftId,
  selectDraftLookupSettled,
  selectDraftSequence,
  selectRawDrafts,
  selectSaveDraftEnabledForFlow,
  selectStaleMinorDraft,
} from '@store/selectors/draft/draft.selectors';
import {
  getDraftPayload,
  getITDraftPayload,
  getMinorDraftPayload,
  getMultiDraftPayload,
} from '@store/selectors/draft/draft-payload.selectors';
import { selectRouterState } from '@store/selectors/router/common/router.selectors';
import {
  selectAllTransactions,
  selectCurrentTransaction,
  selectCurrentTransactionId,
  selectStateTransactionIds,
  selectTransactionById,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  timer,
  withLatestFrom,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  buildFlowActions,
  buildGlobalActions,
  buildTransactionState,
  markFirstNewAccountTransaction,
} from './hydration-mapper';

// Pages whose outgoing Next navigation must NOT persist a draft update.
// Terms pages submit via appianSubmit and only fire Next post-submit (to the
// confirmation page), so advancing from them should never re-save the draft.
const SKIP_DRAFT_UPDATE_PAGES = new Set<string>(['existing', 'new', 'multi-new', 'review-sign']);
const DECEDENT_INFO_PAGES = new Set<string>(['decedent-info', 'decedent-information']);

/** Short next to app-spinner's 3-minute page-level watchdog; guards the minor draft lookup only. */
const MINOR_DRAFT_LOOKUP_TIMEOUT_MS = 60_000;

@Injectable()
export class DraftEffects {
  constructor(
    private actions$: Actions,
    private draftService: DraftService,
    private store: Store<State>,
    private stopPendingEffectsService: StopPendingEffectsService,
  ) {}

  saveNewDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaveNewDraft),
      withLatestFrom(
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectBaseProcessTrackingId),
        this.store.select(selectDraftSequence),
      ),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      exhaustMap(([{ experienceType }, , base, sequence]) => {
        const baseProcessTrackingId = base ?? uuidv4();
        const draftSequence = sequence + 1;
        const processTrackingId = `${baseProcessTrackingId}_${draftSequence}`;
        const communicationChannel = getConfigClientFromRTE().isInternal
          ? CommunicationChannel.PHONE
          : CommunicationChannel.WEB;
        return this.draftService
          .saveNewDraft(processTrackingId, communicationChannel, experienceType)
          .pipe(
            map((response) =>
              SaveNewDraftSuccess({
                draftId: response.draftId,
                expDate: response.expDate,
                baseProcessTrackingId,
                draftSequence,
              }),
            ),
            catchError((error) =>
              of(
                SaveNewDraftFailure(),
                LogError({
                  payload: {
                    eventId: 'error-draft-save-failure',
                    message: `Draft save endpoint answered with error: ${JSON.stringify(sanitizeError(error))}`,
                    loggerCode: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ),
            ),
          );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  reconcileDraftLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaveNewDraftSuccess, AddStateToTransactions),
      withLatestFrom(
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectDraftId),
        this.store.select(selectDraftSequence),
        this.store.select(selectCurrentTransaction),
        this.store.select(selectAllTransactions),
      ),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      map(([, , draftId, draftSequence, currentTransaction, allTransactions]) => {
        const transactions = Array.isArray(allTransactions) ? allTransactions : [];
        const canLink =
          !!draftId &&
          !!currentTransaction &&
          !currentTransaction.draftId &&
          !transactions.some((transaction) => transaction.draftId === draftId);

        return canLink
          ? StampDraftOnTransaction({
              transactionId: currentTransaction.currentTransactionId,
              draftId,
              draftSequence,
            })
          : null;
      }),
      filter((action): action is ReturnType<typeof StampDraftOnTransaction> => action !== null),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  retrieveDrafts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RetrieveDrafts),
      withLatestFrom(this.store.select(selectSaveDraftEnabledForFlow)),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      switchMap(([{ experienceType }]) =>
        this.draftService.retrieveDrafts(DraftType.CHANGE_OF_OWNERSHIP).pipe(
          map((response) => {
            const drafts = this.filterDrafts(response, experienceType);
            const rawDrafts = response.filter(
              (d) => d.draftDetails.experienceType === experienceType,
            );
            return RetrieveDraftsSuccess({ drafts, rawDrafts });
          }),
          catchError((error) =>
            of(
              RetrieveDraftsFailure(),
              LogError({
                payload: {
                  eventId: 'error-draft-retrieve-failure',
                  message: `Draft retrieve endpoint answered with error: ${JSON.stringify(sanitizeError(error))}`,
                  loggerCode: LoggerCodes.NULL_FAILURE,
                },
              }),
            ),
          ),
          // Discards a response that lands after the watchdog fires, so it can't reopen
          // the resume prompt after the client has already failed open.
          takeUntil(this.actions$.pipe(ofType(MinorDraftLookupTimedOut))),
        ),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  /**
   * Cancels a hung minor draft lookup after MINOR_DRAFT_LOOKUP_TIMEOUT_MS so a slow call
   * can't resurface the resume prompt after the client has moved on. MINOR-gated:
   * retrieveDrafts$ is shared with inheritance, which has no comparable pending treatment.
   */
  minorDraftLookupWatchdog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RetrieveDrafts),
      withLatestFrom(this.store.select(flowTypeSelection)),
      filter(([, flowType]) => flowType === FlowTypes.MINOR),
      switchMap(() =>
        timer(MINOR_DRAFT_LOOKUP_TIMEOUT_MS).pipe(
          mergeMap(() =>
            of(
              MinorDraftLookupTimedOut(),
              LogError({
                payload: {
                  eventId: 'error-minor-draft-lookup-timeout',
                  message: `Minor draft lookup did not settle within ${MINOR_DRAFT_LOOKUP_TIMEOUT_MS}ms; cancelling and treating as failed`,
                  loggerCode: LoggerCodes.NULL_FAILURE,
                },
              }),
            ),
          ),
          takeUntil(this.actions$.pipe(ofType(RetrieveDraftsSuccess, RetrieveDraftsFailure))),
        ),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  /**
   * !draftId so returning to transfer-from mid-request is not offered the client's own live
   * draft — declining that prompt hard-deletes the request they are in the middle of.
   */
  lookUpMinorDraftOnFlowEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MinorFlowEntered),
      withLatestFrom(
        this.store.select(flowTypeSelection),
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectDraftId),
      ),
      filter(
        ([, flowType, isSaveDraftEnabled, draftId]) =>
          flowType === FlowTypes.MINOR && isSaveDraftEnabled && !draftId,
      ),
      map(() => RetrieveDrafts({ experienceType: ExperienceType.UGMA_UTMA })),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  discardStaleMinorDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RetrieveDraftsSuccess),
      concatLatestFrom(() => [
        this.store.select(flowTypeSelection),
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectStaleMinorDraft),
      ]),
      filter(
        ([, flowType, isSaveDraftEnabled, staleDraft]) =>
          flowType === FlowTypes.MINOR && isSaveDraftEnabled && !!staleDraft?.allIds.length,
      ),
      switchMap(([, , , staleDraft]) =>
        of(
          DeleteDrafts({ ids: staleDraft!.allIds, skipRetrieve: true }),
          StaleDraftDiscarded(),
          LogInfo({
            payload: {
              eventId: 'draft-stale-discarded',
              message: 'Minor draft discarded: a saved sending account is no longer eligible',
              loggerCode: LoggerCodes.GENERAL_SUCCESS,
            },
          }),
        ),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  /**
   * The redirect counterpart to discardStaleMinorDraft$. Owns its own lookup because the
   * ineligible page resets the store before the response lands.
   */
  discardAllMinorDrafts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaleMinorDraftCleanupRequested),
      concatLatestFrom(() => this.store.select(selectSaveDraftEnabledForFlow)),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      mergeMap(([{ eligibleAccountIds }]) =>
        this.draftService.retrieveDrafts(DraftType.CHANGE_OF_OWNERSHIP).pipe(
          mergeMap((response) => {
            const ids = minorDraftIdsToDiscardOnExit(
              this.filterDrafts(response, ExperienceType.UGMA_UTMA),
              response.filter(
                (draft) => draft.draftDetails.experienceType === ExperienceType.UGMA_UTMA,
              ),
              eligibleAccountIds,
            );

            if (!ids.length) {
              return EMPTY;
            }

            return of(
              DeleteDrafts({ ids, skipRetrieve: true }),
              LogInfo({
                payload: {
                  eventId: 'draft-stale-discarded-on-exit',
                  message: `Minor drafts discarded: client left ineligible before entering the flow (${ids.length} rows)`,
                  loggerCode: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            );
          }),
          // The client is already being redirected and cannot act on this.
          catchError((error) =>
            of(
              LogError({
                payload: {
                  eventId: 'error-draft-stale-cleanup-failure',
                  message: `Stale draft cleanup could not read drafts: ${JSON.stringify(sanitizeError(error))}`,
                  loggerCode: LoggerCodes.NULL_FAILURE,
                },
              }),
            ),
          ),
        ),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  /**
   * These five are every way the lookup can end without a resume — RetrieveDraftsSuccess
   * covers "found nothing", the rest are the later answers to "found one" (or timed out).
   *
   * DraftResumeAccepted is deliberately absent: a resumed draft's id only arrives with
   * HydrateDraftSuccess, so creating here would orphan a duplicate against a null draftId.
   */
  createMinorDraftOnLookupSettled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RetrieveDraftsSuccess,
        RetrieveDraftsFailure,
        MinorDraftLookupTimedOut,
        DraftResumeDeclined,
        HydrateDraftFailure,
        StaleDraftDiscarded,
      ),
      withLatestFrom(
        this.store.select(flowTypeSelection),
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectDraftLookupSettled),
        this.store.select(selectDraftId),
      ),
      filter(
        ([, flowType, isSaveDraftEnabled, lookupSettled, draftId]) =>
          flowType === FlowTypes.MINOR && isSaveDraftEnabled && lookupSettled && !draftId,
      ),
      map(() => SaveNewDraft({ experienceType: ExperienceType.UGMA_UTMA })),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  updateDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateDraft),
      withLatestFrom(
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectDraftId),
        this.store.select(flowTypeSelection),
        this.store.select(selectCurrentTransactionId),
        this.store.select(selectStateTransactionIds),
      ),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      exhaustMap(([, , draftId, flowType, currentTransactionId, transactionIds]) => {
        return this.store.select(this.getDraftPayloadSelector(flowType)).pipe(
          take(1),
          map((payload) =>
            this.extractPayload(flowType, payload, currentTransactionId, transactionIds),
          ),
          exhaustMap((payload) => {
            return this.draftService.updateDraft(draftId || '', payload).pipe(
              map(() => UpdateDraftSuccess()),
              catchError((error) =>
                of(
                  UpdateDraftFailure(),
                  LogError({
                    payload: {
                      eventId: 'error-draft-update-failure',
                      message: `Draft update endpoint answered with error: ${JSON.stringify(sanitizeError(error))}`,
                      loggerCode: LoggerCodes.NULL_FAILURE,
                    },
                  }),
                ),
              ),
            );
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  updateDraftOnContinue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Next),
      withLatestFrom(
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectDraftId),
        this.store.select(flowTypeSelection),
        this.store.select(selectBaseProcessTrackingId),
        this.store.select(selectDraftSequence),
        this.store.select(selectRouterState),
      ),
      filter(
        ([, isSaveDraftEnabled, , , , , routerState]) =>
          // selectSaveDraftEnabledForFlow is already true only for the flows that save drafts
          // (minor, multi-inheritance), so no separate flowType check is needed here.
          isSaveDraftEnabled && !SKIP_DRAFT_UPDATE_PAGES.has(this.getCurrentPage(routerState)),
      ),
      exhaustMap(([, , draftId, flowType, base, sequence]) => {
        if (draftId) {
          return of(UpdateDraft());
        }

        const baseProcessTrackingId = base ?? uuidv4();
        const draftSequence = sequence + 1;
        const processTrackingId = `${baseProcessTrackingId}_${draftSequence}`;
        const communicationChannel = getConfigClientFromRTE().isInternal
          ? CommunicationChannel.PHONE
          : CommunicationChannel.WEB;
        const experienceType = this.getExperienceType(flowType);

        return this.draftService
          .saveNewDraft(processTrackingId, communicationChannel, experienceType)
          .pipe(
            switchMap((response) =>
              of(
                SaveNewDraftSuccess({
                  draftId: response.draftId,
                  expDate: response.expDate,
                  baseProcessTrackingId,
                  draftSequence,
                }),
                UpdateDraft(),
              ),
            ),
            catchError((error) =>
              of(
                SaveNewDraftFailure(),
                LogError({
                  payload: {
                    eventId: 'error-draft-save-retry-failure',
                    message: `Draft retry-create on continue failed: ${JSON.stringify(sanitizeError(error))}`,
                    loggerCode: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ),
            ),
          );
      }),
    ),
  );

  saveDraftOnQuit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SaveDraftOnQuit),
      withLatestFrom(
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(selectDraftId),
        this.store.select(flowTypeSelection),
        this.store.select(selectBaseProcessTrackingId),
        this.store.select(selectDraftSequence),
        this.store.select(selectRouterState),
      ),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      exhaustMap(([, , draftId, flowType, base, sequence, routerState]) => {
        if (draftId) {
          return of(UpdateDraft());
        }

        const currentPage = this.getCurrentPage(routerState);
        if (!DECEDENT_INFO_PAGES.has(currentPage)) {
          return of(UpdateDraftFailure());
        }

        const baseProcessTrackingId = base ?? uuidv4();
        const draftSequence = sequence + 1;
        const processTrackingId = `${baseProcessTrackingId}_${draftSequence}`;
        const communicationChannel = getConfigClientFromRTE().isInternal
          ? CommunicationChannel.PHONE
          : CommunicationChannel.WEB;
        const experienceType = this.getExperienceType(flowType);

        return this.draftService
          .saveNewDraft(processTrackingId, communicationChannel, experienceType)
          .pipe(
            switchMap((response) =>
              of(
                SaveNewDraftSuccess({
                  draftId: response.draftId,
                  expDate: response.expDate,
                  baseProcessTrackingId,
                  draftSequence,
                }),
                UpdateDraft(),
              ),
            ),
            catchError((error) =>
              of(
                SaveNewDraftFailure(),
                LogError({
                  payload: {
                    eventId: 'error-draft-save-on-quit-failure',
                    message: `Draft create on quit failed: ${JSON.stringify(sanitizeError(error))}`,
                    loggerCode: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ),
            ),
          );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  hydrateDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HydrateDraft),
      withLatestFrom(this.store.select(selectRawDrafts)),
      switchMap(([{ draftId }, rawDrafts]) => {
        const clicked = rawDrafts.find((d) => d.draftId === draftId);

        if (!clicked) {
          return of(HydrateDraftFailure());
        }

        try {
          const base = clicked.draftDetails.processTrackingId.replace(/_\d+$/, '');
          const siblings = rawDrafts
            .filter((d) => d.draftDetails.processTrackingId.replace(/_\d+$/, '') === base)
            .sort(
              (a, b) =>
                this.suffixNumber(a.draftDetails.processTrackingId) -
                this.suffixNumber(b.draftDetails.processTrackingId),
            );

          const isMultiInheritance =
            clicked.draftDetails.experienceType === ExperienceType.MULTI_INHERITANCE;

          if (!isMultiInheritance) {
            const result = hydrate(siblings[0].draftDetails);
            return of(
              ...buildGlobalActions(result),
              ...buildFlowActions(result),
              HydrateDraftSuccess({
                draftId,
                baseProcessTrackingId: base,
                draftSequence: this.suffixNumber(clicked.draftDetails.processTrackingId),
              }),
            );
          }

          const states: TransactionState[] = [];
          let highestId = '';
          let highestResult: HydrationResult = {};
          let highestDraftId = '';
          let sequence = 1;

          siblings.forEach((sibling, index) => {
            const result = hydrate(sibling.draftDetails);
            const id = uuidv4();
            states.push(
              buildTransactionState(result, id, {
                draftId: sibling.draftId,
                draftSequence: this.suffixNumber(sibling.draftDetails.processTrackingId),
              }),
            );

            if (index === siblings.length - 1) {
              highestId = id;
              highestResult = result;
              highestDraftId = sibling.draftId;
              sequence = this.suffixNumber(sibling.draftDetails.processTrackingId);
            }
          });

          return of(
            ...buildGlobalActions(highestResult),
            LoadTransactionStates({ states: markFirstNewAccountTransaction(states) }),
            SetTransactionId({ id: highestId }),
            HydrateDraftSuccess({
              draftId: highestDraftId,
              baseProcessTrackingId: base,
              draftSequence: sequence,
            }),
          );
        } catch (error) {
          return of(
            HydrateDraftFailure(),
            LogError({
              payload: {
                eventId: 'error-draft-hydrate-failure',
                message: `Draft hydration failed: ${JSON.stringify(sanitizeError(error))}`,
                loggerCode: LoggerCodes.NULL_FAILURE,
              },
            }),
          );
        }
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  deleteDrafts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteDrafts),
      withLatestFrom(
        this.store.select(selectSaveDraftEnabledForFlow),
        this.store.select(flowTypeSelection),
      ),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      filter(([{ ids }]) => ids.length > 0),
      exhaustMap(([{ ids, skipRetrieve, skipDocumentCleanup }, , flowType]) => {
        const experienceType = this.getExperienceType(flowType);
        const retrieveActions = skipRetrieve ? [] : [RetrieveDrafts({ experienceType })];

        return forkJoin(
          ids.map((id) => this.draftService.deleteDraft(id, skipDocumentCleanup)),
        ).pipe(
          switchMap(() => of(DeleteDraftsStarted(), DeleteDraftsSuccess(), ...retrieveActions)),
          catchError((error) =>
            of(
              DeleteDraftsStarted(),
              DeleteDraftsFailure(),
              ...retrieveActions,
              LogError({
                payload: {
                  eventId: 'error-draft-delete-failure',
                  message: `Draft delete endpoint answered with error: ${JSON.stringify(sanitizeError(error))}`,
                  loggerCode: LoggerCodes.NULL_FAILURE,
                },
              }),
            ),
          ),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  changeActiveDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveDraftChanged),
      withLatestFrom(this.store.select(selectSaveDraftEnabledForFlow)),
      filter(([, isSaveDraftEnabled]) => isSaveDraftEnabled),
      switchMap(([{ transactionId }]) =>
        this.store.select(selectTransactionById(transactionId)).pipe(
          take(1),
          map((transaction) => {
            if (!transaction?.draftId || transaction.draftSequence === undefined) {
              return ActiveDraftChangedFailure();
            }

            return ActiveDraftChangedSuccess({
              draftId: transaction.draftId,
              draftSequence: transaction.draftSequence,
            });
          }),
        ),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  private getCurrentPage(routerState: RouterStateUrl | undefined): string {
    if (!routerState?.url) {
      return '';
    }

    const segments = routerState.url.split('?')[0].split('/');
    const paramsCount = Object.keys(routerState.params).length;

    return segments[segments.length - 1 - paramsCount];
  }

  private getDraftPayloadSelector(flowType: FlowTypes | null): MemoizedSelector<State, any> {
    switch (flowType) {
      case FlowTypes.MULTI_INHERITANCE:
        return getMultiDraftPayload;
      case FlowTypes.MINOR:
        return getMinorDraftPayload;
      case FlowTypes.INTERNAL_TRANSFER:
      case FlowTypes.INTERNAL:
      case FlowTypes.DIVORCE:
        return getITDraftPayload;
      default:
        return getDraftPayload;
    }
  }

  private getExperienceType(flowType: FlowTypes | null): ExperienceType {
    if (flowType === FlowTypes.MINOR) {
      return ExperienceType.UGMA_UTMA;
    }

    if (flowType === FlowTypes.MULTI_INHERITANCE) {
      return ExperienceType.MULTI_INHERITANCE;
    }

    return ExperienceType.NONE;
  }

  private extractPayload(
    flowType: FlowTypes | null,
    payload: any,
    currentTransactionId: string,
    transactionIds: string[] | number[],
  ): any {
    if (flowType === FlowTypes.MULTI_INHERITANCE) {
      const currentIndex = transactionIds.indexOf(currentTransactionId as never);

      return payload?.payloads?.[currentIndex >= 0 ? currentIndex : 0] || {};
    }

    return payload;
  }

  filterDrafts(drafts: RetrieveDraftItem[], experienceType: ExperienceType): SaveDraftItem[] {
    const filtered = drafts.filter((draft) => draft.draftDetails.experienceType === experienceType);
    const grouped = this.groupByBase(filtered);

    return [...grouped.values()]
      .map(([representative, allInGroup]) => ({
        id: representative.draftId,
        allIds: allInGroup.map((d) => d.draftId),
        initiator:
          representative.communicationChannel === CommunicationChannel.WEB
            ? DraftInitiator.CLIENT
            : DraftInitiator.CREW,
        dateStarted: representative.createdDateTime,
        requestType: 'Inheritance request',
      }))
      .sort((a, b) => this.dateStartedTime(b.dateStarted) - this.dateStartedTime(a.dateStarted));
  }

  // A missing or invalid dateStarted gives NaN, which the sort comparator treats as "equal" —
  // fall back to negative infinity so these drafts always sort to the bottom, never mixed in.
  private dateStartedTime(dateStarted: string): number {
    const time = new Date(dateStarted).getTime();

    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
  }

  private groupByBase(
    drafts: RetrieveDraftItem[],
  ): Map<string, [RetrieveDraftItem, RetrieveDraftItem[]]> {
    const byBase = new Map<string, [RetrieveDraftItem, RetrieveDraftItem[]]>();

    drafts.forEach((draft) => {
      const base = draft.draftDetails.processTrackingId.replace(/_\d+$/, '');
      const existing = byBase.get(base);

      if (!existing) {
        byBase.set(base, [draft, [draft]]);
      } else {
        const [currentRep, allInGroup] = existing;
        allInGroup.push(draft);
        if (
          this.suffixNumber(draft.draftDetails.processTrackingId) <
          this.suffixNumber(currentRep.draftDetails.processTrackingId)
        ) {
          byBase.set(base, [draft, allInGroup]);
        }
      }
    });

    return byBase;
  }

  private suffixNumber(processTrackingId: string): number {
    const match = processTrackingId.match(/_(\d+)$/);

    // 0 represents pre-suffix legacy drafts; new drafts always start at _1.
    return match ? Number(match[1]) : 0;
  }
}
